import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import {
	CurrentSectionData,
	IncentiveTankData,
	MillOverviewData,
	ProductionChartData,
	RollingTotalData,
	UpcomingSectionData,
} from "../models/MillOverview";

const moment = getMomentTz();

// SQL Server returns datetimes without timezone suffix → parse as UTC
// to avoid the browser interpreting them as local time.
const parseUtc = (s: unknown): Date | null =>
	s ? moment.utc(s as string).toDate() : null;

// [FAB].[Get2MillOverview] passes through the mill's [M2].[GetMillOverview2]
// result sets unchanged. Only the sets this page renders are parsed:
// 0 Rolling Total, 3 Current Section, 4 Upcoming Section, 9 Production Chart,
// 10 IsInDelay, 13 Incentive.
export const getMillOverview = async (
	signal?: AbortSignal,
): Promise<MillOverviewData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[FAB].[Get2MillOverview]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching mill overview data");
	}

	const tables = resp.data.tables || [];

	const rollingTotalRow = tables[0]?.rows?.[0] || {};
	const rollingTotal: RollingTotalData = {
		BilletTonsCharged: rollingTotalRow.BilletTonsCharged ?? 0,
		BilletTonsChargedScheduled: rollingTotalRow.BilletTonsChargedScheduled ?? 0,
		BilletPcsCharged: rollingTotalRow.BilletPcsCharged ?? 0,
		BilletPcsChargedScheduled: rollingTotalRow.BilletPcsChargedScheduled ?? 0,
		RolledTons: rollingTotalRow.RolledTons ?? 0,
		RolledTonsScheduled: rollingTotalRow.RolledTonsScheduled ?? 0,
		FinishedTons: rollingTotalRow.FinishedTons ?? null,
		FinishedTonsScheduled: rollingTotalRow.FinishedTonsScheduled ?? 0,
		FinishedBundles: rollingTotalRow.FinishedBundles ?? null,
		MillYield: rollingTotalRow.MillYield ?? 0,
		MillYieldScheduled: rollingTotalRow.MillYieldScheduled ?? 0,
		FinishedYield: rollingTotalRow.FinishedYield ?? null,
		FinishedYieldScheduled: rollingTotalRow.FinishedYieldScheduled ?? 0,
		TotalCobbles: rollingTotalRow.TotalCobbles ?? 0,
		Production: rollingTotalRow.Production ?? 0,
		Delays: rollingTotalRow.Delays ?? 0,
		HoursActual: rollingTotalRow.HoursActual ?? 0,
		HoursScheduled: rollingTotalRow.HoursScheduled ?? 0,
		TPHActual: rollingTotalRow.TPHActual ?? 0,
		TPHBudgeted: rollingTotalRow.TPHBudgeted ?? 0,
		TPHTargeted: rollingTotalRow.TPHTargeted ?? null,
	};

	const currentSectionRow = tables[3]?.rows?.[0] || {};
	const currentSection: CurrentSectionData = {
		CurrentSectionName: currentSectionRow.CurrentSectionName ?? "",
		ScheduledHours: currentSectionRow.ScheduledHours ?? null,
		CurrentHours: currentSectionRow.CurrentHours ?? null,
		RemainingHours: currentSectionRow.RemainingHours ?? null,
		ProjectedRollOut: parseUtc(currentSectionRow.ProjectedRollOut),
		VarianceHours: currentSectionRow.VarianceHours ?? null,
		EventStart: parseUtc(currentSectionRow.EventStart),
		ScheduledEnd: parseUtc(currentSectionRow.ScheduledEnd),
	};

	const upcomingSectionRow = tables[4]?.rows?.[0] || {};
	const upcomingSection: UpcomingSectionData = {
		UpcomingSectionName: upcomingSectionRow.UpcomingSectionName ?? "",
		ProjectedBreakin: parseUtc(upcomingSectionRow.ProjectedBreakin),
	};

	const productionChart: ProductionChartData[] = (tables[9]?.rows || []).map(
		(row: Record<string, unknown>) => ({
			Time: parseUtc(row.Time),
			TargetTPH: (row.TargetTPH as number) ?? null,
			BudgetTPH: (row.BudgetTPH as number) ?? 0,
			Actual: (row.Actual as number) ?? null,
			CumulativeAvg: (row.CumulativeAvg as number) ?? null,
		}),
	);

	const isInDelay = Boolean(tables[10]?.rows?.[0]?.IsInDelay);

	const incentiveRow = tables[13]?.rows?.[0];
	const incentiveTank: IncentiveTankData | null = incentiveRow
		? {
				Finished: incentiveRow.Finished ?? 0,
				Target: incentiveRow.Target ?? 0,
				IncPercent: incentiveRow.IncPercent ?? null,
				IncRed: incentiveRow.IncRed,
				IncOrange: incentiveRow.IncOrange,
				IncYellow: incentiveRow.IncYellow,
				IncGreenSoft: incentiveRow.IncGreenSoft,
				IncGreenNormal: incentiveRow.IncGreenNormal,
				IncGreenHard: incentiveRow.IncGreenHard,
			}
		: null;

	return {
		RollingTotal: rollingTotal,
		CurrentSection: currentSection,
		UpcomingSection: upcomingSection,
		ProductionChart: productionChart,
		IsInDelay: isInDelay,
		IncentiveTank: incentiveTank,
	};
};
